import type { CSSProperties } from "react";
import { useToasts, dismissToast, type ToastTone } from "@/lib/toast";

const toneColors: Record<ToastTone, { bg: string; fg: string; dot: string }> = {
  info: { bg: "var(--surface-elev)", fg: "var(--ink-1)", dot: "#5B7A95" },
  success: { bg: "var(--surface-elev)", fg: "var(--ink-1)", dot: "#3F8F65" },
  error: { bg: "var(--surface-elev)", fg: "var(--danger)", dot: "var(--danger)" },
};

const containerStyle: CSSProperties = {
  position: "fixed",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  zIndex: 500,
  pointerEvents: "none",
};

const baseToastStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid var(--hairline-strong)",
  boxShadow: "0 8px 24px rgba(13,62,99,0.18), 0 2px 6px rgba(13,62,99,0.08)",
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
  fontSize: 13.5,
  pointerEvents: "auto",
  cursor: "pointer",
  animation: "flow-fade-in 140ms ease-out",
  maxWidth: 360,
};

// A 44 px touch target (WCAG 2.5.5) for "Neu laden" / "Rückgängig" on the phone. The
// negative margins let the button reach into the toast's 10 px / 14 px padding, so it
// spans the toast's full height without making the toast taller.
const actionBtnStyle: CSSProperties = {
  background: "none",
  border: "none",
  padding: "0 10px",
  margin: "-10px -8px -10px -6px",
  minHeight: 44,
  minWidth: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
  fontSize: 13.5,
  fontWeight: 600,
  color: "var(--accent, #3B82F6)",
  cursor: "pointer",
  flexShrink: 0,
};

export function Toaster() {
  const toasts = useToasts();
  return (
    <div style={containerStyle} aria-live="polite">
      {toasts.map((tst) => {
        const tone = toneColors[tst.tone];
        return (
          <div
            key={tst.id}
            tabIndex={0}
            onClick={() => dismissToast(tst.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                dismissToast(tst.id);
              }
            }}
            style={{ ...baseToastStyle, background: tone.bg, color: tone.fg }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: tone.dot,
                flexShrink: 0,
              }}
            />
            <span>{tst.message}</span>
            {tst.action ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  tst.action?.onClick();
                  dismissToast(tst.id);
                }}
                style={actionBtnStyle}
              >
                {tst.action.label}
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
